import { DestroyRef, Directive, inject, Injector, Signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder } from '@angular/forms';
import { NotificationService } from '@app/core/services/notification.service';
import { DialogComponent } from '@app/shared/dialog/dialog-component.directive';
import { UnsavedChangesService } from '@app/shared/services/unsaved-changes-service';
import { componentState } from '@app/utils/component-state';
import { PortalError } from '@app/utils/error/portal-error';
import { watch } from '@app/utils/signals/watch';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';

@Directive()
export class UpdateDialogComponent<Input = any, Output = any> extends DialogComponent<Input, Output> {
  error: PortalError | null = null;
  saving: boolean = false;
  savingId?: string | null = null;

  protected destroyRef = inject(DestroyRef);
  protected fb = inject(FormBuilder);
  protected injector = inject(Injector);
  protected notificationService = inject(NotificationService);
  protected state = componentState();
  protected unsavedChangesService = inject(UnsavedChangesService);

  protected requiredUnsavedConfirmation(required: boolean): void {
    if (required) {
      this.unsavedChangesService.askForConfirmation('modal');
    } else {
      this.unsavedChangesService.reset('modal');
    }
  }

  protected setSaving =
    (saving: boolean, id: string | null = null) =>
    () => {
      this.saving = saving;
      this.savingId = id;
    };

  /**
   * This is only here so typescript can infer the type of the generic argument.
   *
   * Reasoning: Typescript can infer types only if they are used. The problem with the generic type Input is that
   * the component should implement it (since it is an interface defining the input properties of the dialog). Since
   * that is not possible (TS2422: A class can only implement an object type or intersection of object types with statically known members.)
   * we use that workaround to enable the inference of Input.
   *
   * See answer here: https://github.com/microsoft/TypeScript/issues/360#issuecomment-52518667
   */
  '__element type'?: Input;

  constructor(ngbActiveModal?: NgbActiveModal) {
    super(ngbActiveModal);

    this.destroyRef.onDestroy(() => this.requiredUnsavedConfirmation(false));
  }

  protected askForConfirmationIfDirty(dirty: Signal<boolean>): void {
    watch([dirty], ([dirty]) => {
      this.requiredUnsavedConfirmation(dirty);
    });
  }

  /**
   * Subscribes to the given observable and updates the fields `error` and
   * `saving` accordingly. Closes the dialog and shows a successful notification
   * if the observable emits.
   */
  protected handleSubmit(observable: Observable<Output>): void;

  /**
   * Subscribes to the given observable and updates the fields `error` and
   * `saving` accordingly. Closes the dialog and shows a successful notification
   * if the observable emits.
   *
   * @param observable
   * @param successfulI18nKey
   */
  protected handleSubmit(observable: Observable<Output>, successfulI18nKey: string): void;

  /**
   * Subscribes to the given observable and updates the fields `error` and
   * `saving` accordingly. When the observable emits it will close the dialog
   * and show a successful toast.
   */
  protected handleSubmit(observable: Observable<Output>, options: HandleSubmitOptions): void;

  protected handleSubmit(
    observable: Observable<Output>,
    optionsOrSuccessfulI18nKey?: string | HandleSubmitOptions
  ): void {
    if (typeof optionsOrSuccessfulI18nKey === 'string') {
      optionsOrSuccessfulI18nKey = {
        entityI18nKey: optionsOrSuccessfulI18nKey,
      };
    }

    const { entityI18nKey, i18nKey, id } = optionsOrSuccessfulI18nKey ?? {};

    this.error = null;
    this.saving = true;
    this.savingId = id;

    observable.pipe(finalize(this.setSaving(false, id)), takeUntilDestroyed(this.destroyRef)).subscribe({
      error: (error) => {
        this.error = error;
      },
      next: (result) => {
        this.unsavedChangesService.reset('modal');
        this.close(result);

        if (entityI18nKey != null) {
          this.notificationService.showSuccessfulSave(entityI18nKey);
        } else if (i18nKey != null) {
          this.notificationService.showSuccess(i18nKey);
        } else {
          this.notificationService.showSuccess('generic.success');
        }
      },
    });
  }
}

interface HandleSubmitOptions {
  /**
   * Shows the generic 'common.update.successful' message ("Saved {{ name }}") and replaces name with the translated entityI18nKey.
   */
  entityI18nKey?: string;

  /**
   * Shows this translated i18nKey instead of the generic 'common.update.successful'.
   */
  i18nKey?: string;

  /**
   * Sets the field {@link savingId} to the given value. This makes it
   * possible for a dialog to have multiple save actions that should
   * all be disabled when saving but only a specific one should show
   * a spinning icon to indicate which action is being performed.
   */
  id?: string;
}
